import type {
  Currency,
  Game,
  SubGameCategory,
  User,
} from '@repo-level/core-front';
import {
  AuthContext,
  CommonContext,
  GamesContext,
  randomNumber,
  updateBonusSettings,
  useApi,
} from '@repo-level/core-front';
import type { CmsABTest } from 'api/cms/abTests';
import { cmsABTests } from 'api/cms/abTests';
import type { CMSImageBase } from 'api/cms/base';
import { CMSGroupMode } from 'api/cms/base';
import type { Bonus, BonusGroup, BonusI, IPromocode } from 'api/cms/bonuses';
import {
  bonuses as fetchBonuses,
  bonusGroups as fetchBonusGroups,
  promocodes as fetchPromocodes,
} from 'api/cms/bonuses';
import type {
  CMSGameCategory,
  SeparatedCategoriesSection,
} from 'api/cms/gameCategories';
import {
  gameCategories,
  separatedCategoriesSection,
} from 'api/cms/gameCategories';
import { gameCovers } from 'api/cms/gameCovers';
import type { CMSGameTheme } from 'api/cms/gameThemes';
import { gameThemes } from 'api/cms/gameThemes';
import type { PaymentMethodsApi } from 'api/cms/paymentMethods';
import { paymentMethods } from 'api/cms/paymentMethods';
import type { ThirdPartySummary } from 'api/cms/thirdPartySummaries';
import { getThirdPartySummaries } from 'api/cms/thirdPartySummaries';
import type {
  CompointsRatesMoney,
  LootboxItem,
  PlayerCompoints,
} from 'api/common';
import { compPointsRatesMoney, playerCompoints } from 'api/common';
import type { Jackpots } from 'api/games';
import { jackpots as fetchJackpots } from 'api/games';
import {
  bonusesToDepositCodes,
  bonusGroupsToDepositCodes,
  sortCovers,
} from 'contexts/addons/helper';
import {
  filterByCountries,
  filterByGroupAndDuration,
  filterBySchedule,
} from 'helpers/cms';
import {
  filterBonuses,
  filterBonusGroups,
  filterPromocodes,
} from 'helpers/common';
import { getEnabledCurrenciesByCountry } from 'helpers/currency';
import type { UpdatedGameCategory } from 'helpers/game';
import {
  GameRecommendationCategories,
  getLocalizedCategoryId,
} from 'helpers/game';
import { LocalStorage } from 'helpers/localStorage';
import { useUserWageringBonuses } from 'hooks/bonuses';
import { CookieType, pickProperties, useCookie } from 'hooks/common';
import type { CustomOffer } from 'hooks/customOffer';
import { useCustomOffer } from 'hooks/customOffer';
import { usePaymentLanguage } from 'hooks/payments';
import { useSyncABTestsUserGroup } from 'modules/abTesting/hooks/useSyncABTestsUserGroup';
import type { ECrystalStatus } from 'modules/crystal/hooks';
import type { WheelStatusEnum } from 'modules/wheel/components/helpers';
import moment from 'moment';
import { ascend, filter, findLast, isNil, map, pipe, prop, sort } from 'ramda';
import type { FC } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Category } from '../../@repo-level/core-front/api/games';

const SHOW_EMAIL_POPUP_AGAIN_AFTER_DAYS = 7;

export const lastSeenEmailConfirmationPopup = new LocalStorage<string, null>(
  'last-seen-email-confirmation-popup',
  null
);

export const isAuroraCrystalStartedLS = new LocalStorage<string, string | null>(
  'is-aurora-crystal-started',
  null
);

const CONFIRMATION_EMAIL_ROUTE = '/users/confirmation';

const initialParamsForCustomBonus = {
  frequency: null,
  wager: null,
  minDep: null,
  additionalInfo: null,
  depNumber: null,
  fsAmount: null,
  game: null,
  maxBonusAmount: null,
  maxWinFs: null,
  wagerTime: null,

  cashier: true,
  promoPage: true,
  tcPage: true,

  showGuestToggle: true,
  groupsIncludeMode: CMSGroupMode.AND,
  groupsInclude: null,
  groupsExcludeMode: CMSGroupMode.AND,
  groupsExclude: null,
  durationFrom: null,
  durationTo: null,
};

export interface GameCovers {
  [key: string]: { cover: CMSImageBase };
}

export interface AddonsContextData {
  covers: GameCovers;
  pendingCovers: boolean;
  gameCategoriesList: UpdatedGameCategory[];
  gameCategoriesMap: Map<string, UpdatedGameCategory>;
  pendingGameCategoriesList: boolean;
  gameThemes: CMSGameTheme[];
  pendingGameThemes: boolean;
  depositCodes: string[];
  bonuses: Bonus[];
  pendingBonuses: boolean;
  bonusGroups: BonusGroup[];
  pendingBonusGroups: boolean;
  jackpots: Jackpots | undefined;
  customOffer: CustomOffer | undefined;
  enableCurrencies: Currency[];
  thirdPartySummaries: ThirdPartySummary[] | undefined;
  pendingThirdPartySummaries: boolean;
  separatedCategoriesSectionData: SeparatedCategoriesSection | undefined;
  separatedCategoriesSectionPending: boolean;
  depositBonuses: BonusI[];
  compointsRatesMoney: CompointsRatesMoney[] | undefined;
  pendingCompointsRatesMoney: boolean;
  playerCompoints: PlayerCompoints | undefined;
  fetchPlayerCompoints: () => void;
  pendingPlayerCompoints: boolean;
  isEmailConfirmationPopup: boolean;
  emailConfirmationPopupHeight: number;
  setEmailConfirmationPopupSeen: () => void;
  setEmailConfirmationPopupHeight: (height: number) => void;
  getFilteredByUserWalletsGames: (gamesToFilter: Game[]) => Game[];

  winningPrizeIndex: number;
  setWinningPrizeIndex: (winningPrizeIndex: number) => void;
  lootboxItem: LootboxItem | null;
  setLootboxItem: (lootboxItem: LootboxItem | null) => void;
  wheelStatus: WheelStatusEnum | null;
  setWheelStatus: (wheelStatus: WheelStatusEnum | null) => void;

  autoRegistrationData: Partial<User> | null;
  setAutoRegistrationData: (data: Partial<User> | null) => void;

  cmsABTests: CmsABTest[];
  pendingCmsAbTests: boolean;

  gameOpenBackUrl: string | null;
  setGameOpenBackUrl: (gameOpenBackUrl: string | null) => void;

  localizedTopCategoryId: string;
  localizedLuckyHitsCategoryId: string;

  cmsPaymentMethods: PaymentMethodsApi | undefined;

  crystalStatus: ECrystalStatus | null;
  setCrystalStatus: (status: ECrystalStatus | null) => void;
  isAuroraCrystalStarted: boolean;
  setIsAuroraCrystalStarted: (state: boolean) => void;

  handleChangeReceiveBonuses: () => void;

  pendingUpdateBonusSettings: boolean;

  promocodes?: IPromocode[];
  pendingPromocodes: boolean;
}

export const AddonsContext = createContext<AddonsContextData>(null as any);

export const AddonsContextProvider: FC = ({ children }) => {
  const {
    i18n: { language },
  } = useTranslation();

  const [winningPrizeIndex, setWinningPrizeIndex] = useState(0);
  const [lootboxItem, setLootboxItem] = useState<LootboxItem | null>(null);
  const [wheelStatus, setWheelStatus] = useState<WheelStatusEnum | null>(null);
  const [crystalStatus, setCrystalStatus] = useState<ECrystalStatus | null>(
    null
  );
  const [gameOpenBackUrl, setGameOpenBackUrl] = useState<string | null>(null);
  const [isAuroraCrystalStarted, setIsAuroraCrystalStarted] = useState(
    isAuroraCrystalStartedLS.get() === 'true'
  );

  const { currentIp, currencies } = useContext(CommonContext);
  const {
    user,
    isAuthenticated,
    pendingUser,
    wallets,
    updateUser,
    refreshUser,
  } = useContext(AuthContext);
  const { categories: defaultCategories, pendingCategories } =
    useContext(GamesContext);

  usePaymentLanguage(language);

  useCookie({
    name: 'userDetails',
    value: {
      type: CookieType.Object,
      data: pickProperties(user, [
        'id',
        'email',
        'statuses',
        'language',
        'country',
      ]),
    },
    days: 90,
    encryptKey: 'QExMyj2jKBnWDGpv',
    withReplace: true,
  });

  const [autoRegistrationData, setAutoRegistrationData] =
    useState<Partial<User> | null>(null);

  const [requestGameCovers, { data: covers, pending: pendingCovers }] =
    useApi(gameCovers);
  const [
    requestGameCategories,
    { data: gameCategoriesCMS, pending: pendingGameCategoriesCMS },
  ] = useApi(gameCategories);

  const [
    requestGameThemes,
    { data: gameThemesCMS, pending: pendingGameThemesCMS },
  ] = useApi(gameThemes);

  const [requestCMSPaymentMethods, { data: cmsPaymentMethods }] =
    useApi(paymentMethods);

  const [requestJackpots, { data: jackpots }] = useApi(fetchJackpots);

  const [
    requestBonusGroups,
    { data: bonusGroupsData, pending: pendingBonusGroups },
  ] = useApi(fetchBonusGroups);

  const [requestBonuses, { data: bonusesData, pending: pendingBonuses }] =
    useApi(fetchBonuses);

  const [
    requestPromocodes,
    { data: promocodesData, pending: pendingPromocodes },
  ] = useApi(fetchPromocodes);

  const [
    requestSeparatedCategoriesSection,
    {
      data: separatedCategoriesSectionData,
      pending: separatedCategoriesSectionPending,
    },
  ] = useApi(separatedCategoriesSection);

  const [
    requestCmsABTests,
    { data: cmsABTestsData, pending: pendingCmsAbTests },
  ] = useApi(cmsABTests);

  const [
    requestCompointsRatesMoney,
    { pending: pendingCompointsRatesMoney, data: compointsRatesMoneyData },
  ] = useApi(compPointsRatesMoney);

  const [requestUpdateBonusSettings, { pending: pendingUpdateBonusSettings }] =
    useApi(updateBonusSettings);

  const [playerCompointsS, setPlayerCompointsS] = useState<PlayerCompoints>();

  const [
    requestPlayerCompoints,
    { pending: pendingPlayerCompoints, data: playerCompointsData },
  ] = useApi(playerCompoints);

  const { hasWageringBonus } = useUserWageringBonuses();

  const fetchPlayerCompoints = useCallback(() => {
    isAuthenticated && requestPlayerCompoints();
  }, [isAuthenticated, requestPlayerCompoints]);

  useEffect(() => {
    fetchPlayerCompoints();
  }, [fetchPlayerCompoints]);

  useEffect(() => {
    setPlayerCompointsS(playerCompointsData);
  }, [playerCompointsData]);

  useEffect(() => {
    requestBonuses(language);
    requestPromocodes(language);
    requestBonusGroups(language);
    requestGameCategories(language);
    requestGameThemes(language);
    requestSeparatedCategoriesSection(language);
    requestCmsABTests();
  }, [
    requestBonusGroups,
    requestBonuses,
    requestPromocodes,
    requestGameCategories,
    requestGameThemes,
    language,
    requestSeparatedCategoriesSection,
    requestCmsABTests,
  ]);

  useEffect(() => {
    requestGameCovers();
    requestJackpots();
  }, [requestGameCovers, requestJackpots]);

  useEffect(() => {
    if (!isAuthenticated) return;
    requestCompointsRatesMoney();
  }, [isAuthenticated, requestCompointsRatesMoney]);

  useEffect(() => {
    requestCMSPaymentMethods(language);
  }, [language, requestCMSPaymentMethods]);

  const customOffer = useCustomOffer();

  const bonuses = useMemo(() => {
    const customBonuses = (customOffer?.bonuses?.map((bonus) => ({
      id: randomNumber(100_000, 1_000_000),
      ...initialParamsForCustomBonus,
      ...bonus,
    })) || []) as Bonus[];

    const mergeBonuses = [...customBonuses, ...(bonusesData || [])];
    return filterBonuses(mergeBonuses, user) as Bonus[];
  }, [bonusesData, user, customOffer]);

  const promocodes = useMemo(() => {
    if (!promocodesData) return;
    return filterPromocodes(promocodesData, user);
  }, [promocodesData, user]);

  const [
    requestThirdPartySummaries,
    { data: thirdPartySummaries, pending: pendingThirdPartySummaries },
    { reset: resetThirdPartySummaries },
  ] = useApi(getThirdPartySummaries);

  const bonusGroups = useMemo(
    () => filterBonusGroups(bonusGroupsData || [], user),
    [bonusGroupsData, user]
  );

  const sortedCovers = useMemo(() => sortCovers(covers || []), [covers]);

  const depositCodes = useMemo(
    () => [
      ...bonusesToDepositCodes(bonusesData || []),
      ...bonusGroupsToDepositCodes(bonusGroupsData || []),
    ],
    [bonusesData, bonusGroupsData]
  );

  const handleChangeReceiveBonuses = useCallback(async () => {
    if (!user) return;

    const previousValue = user.canIssueBonuses;
    const newValue = !previousValue;

    updateUser({ canIssueBonuses: newValue });

    const { error } = await requestUpdateBonusSettings(newValue).request;

    if (error) {
      updateUser({ canIssueBonuses: previousValue });
      return;
    }

    refreshUser();
  }, [user, requestUpdateBonusSettings, updateUser, refreshUser]);

  const gameCategoriesList: UpdatedGameCategory[] = useMemo(
    () =>
      pipe<
        CMSGameCategory[],
        CMSGameCategory[],
        UpdatedGameCategory[],
        UpdatedGameCategory[],
        UpdatedGameCategory[],
        UpdatedGameCategory[],
        UpdatedGameCategory[]
      >(
        filter(({ categoryId }: CMSGameCategory) => {
          if (
            categoryId === GameRecommendationCategories.Favorites ||
            categoryId === GameRecommendationCategories.Recent
          )
            return isAuthenticated;

          if (categoryId === Category.WAGERING && !hasWageringBonus) {
            return false;
          }

          return defaultCategories.has(categoryId);
        }),
        map(
          ({
            title,
            filterTitle,
            categoryId,
            order,
            groupsExcludeMode,
            groupsExclude,
            groupsInclude,
            groupsIncludeMode,
            durationFrom,
            durationTo,
            countryCodesExclude,
            countryCodesInclude,
            scheduler,
            description,
            icon,
            showOnLobby,
            subCategories,
          }: CMSGameCategory) => {
            const showGuestToggle = !groupsInclude?.trim().length;

            const sortedSubCategories = sort<SubGameCategory>(
              ascend(prop('order'))
            )(subCategories || []);

            return {
              id: categoryId,
              title,
              filterTitle,
              description,
              icon: icon?.url,
              href: `/search-games/${categoryId}`,
              order: isNil(order) ? defaultCategories.size - 1 : order,
              groupsIncludeMode: groupsIncludeMode || CMSGroupMode.AND,
              groupsInclude,
              groupsExcludeMode: groupsExcludeMode || CMSGroupMode.AND,
              groupsExclude,
              durationFrom,
              durationTo,
              showGuestToggle,
              showOnLobby,
              countryCodesExclude,
              countryCodesInclude,
              scheduler,
              subCategories: sortedSubCategories,
            };
          }
        ),
        filterBySchedule,
        filterByGroupAndDuration<UpdatedGameCategory>({ user }),
        filterByCountries<UpdatedGameCategory>({
          countryCode: currentIp?.countryCode,
        }),
        sort(ascend(prop('order')))
      )(gameCategoriesCMS || []),
    [
      user,
      currentIp?.countryCode,
      gameCategoriesCMS,
      isAuthenticated,
      defaultCategories,
      hasWageringBonus,
    ]
  );

  const sortedGameThemes: CMSGameTheme[] = useMemo(
    () =>
      pipe<CMSGameTheme[], CMSGameTheme[]>(sort(ascend(prop('order'))))(
        gameThemesCMS || []
      ),
    [gameThemesCMS]
  );

  const gameCategoriesMap = useMemo(
    () => new Map(gameCategoriesList.map((cat) => [cat.id, cat])),
    [gameCategoriesList]
  );

  const localizedTopCategoryId = useMemo(
    () => getLocalizedCategoryId(gameCategoriesList, Category.TOP),
    [gameCategoriesList]
  );

  const localizedLuckyHitsCategoryId = useMemo(
    () => getLocalizedCategoryId(gameCategoriesList, Category.LUCKY_HITS),
    [gameCategoriesList]
  );

  const enableCurrencies = useMemo(() => {
    if (currentIp) {
      return getEnabledCurrenciesByCountry(currencies, currentIp.countryCode);
    }
    return [];
  }, [currencies, currentIp]);

  useEffect(() => {
    if (user?.id == null) {
      resetThirdPartySummaries();
      return;
    }

    requestThirdPartySummaries(user.id);
  }, [user?.id, resetThirdPartySummaries, requestThirdPartySummaries]);

  const depositBonuses = useMemo(() => {
    const bonusGroupsT = bonusGroups.flatMap(
      ({ bonuses, background, character }) =>
        bonuses
          .filter(({ cashier, code, isActive }) => cashier && code && isActive)
          .map((bonus) => ({ ...bonus, background, character }))
    );

    const activeBonusInGroup = findLast(
      ({ isActive }) => !!isActive,
      bonusGroupsT
    );

    const allBonuses = [
      ...(activeBonusInGroup ? [activeBonusInGroup] : []),
      ...bonuses.filter(({ cashier, code }) => cashier && code),
    ] as BonusI[];

    return allBonuses.sort((a, b) => a.order - b.order);
  }, [bonuses, bonusGroups]);

  const [isEmailConfirmationPopup, setIsEmailConfirmationPopup] =
    useState(false);

  const [emailConfirmationPopupHeight, setEmailConfirmationPopupHeight] =
    useState(0);

  const setEmailConfirmationPopupSeen = () => {
    lastSeenEmailConfirmationPopup.replace(moment().format());
    setIsEmailConfirmationPopup(false);
  };

  useEffect(() => {
    if (
      !isAuthenticated ||
      pendingUser ||
      user?.confirmedAt ||
      window.location.pathname === CONFIRMATION_EMAIL_ROUTE
    ) {
      setIsEmailConfirmationPopup(false);

      if (
        user?.confirmedAt ||
        window.location.pathname === CONFIRMATION_EMAIL_ROUTE
      )
        localStorage.removeItem(lastSeenEmailConfirmationPopup.key);

      return;
    }

    if (isAuthenticated && !user?.confirmedAt) {
      const isEmailConfirmationPopup =
        !lastSeenEmailConfirmationPopup.get() ||
        moment().diff(lastSeenEmailConfirmationPopup.get(), 'days') >=
          SHOW_EMAIL_POPUP_AGAIN_AFTER_DAYS;

      setIsEmailConfirmationPopup(isEmailConfirmationPopup);

      return;
    }
  }, [currentIp, isAuthenticated, pendingUser, user]);

  const getFilteredByUserWalletsGames = useCallback(
    (gamesToFilter: Game[]) => {
      if (!user) return gamesToFilter;

      const userCurrencies = wallets.map(({ currency }) => currency);

      const availableByCurrencyGames = gamesToFilter.filter(({ real }) => {
        const availableGameCurrencies = Object.keys(real);

        return availableGameCurrencies.some((currency) =>
          userCurrencies.includes(currency)
        );
      });

      return availableByCurrencyGames;
    },
    [user, wallets]
  );

  useSyncABTestsUserGroup();

  return (
    <AddonsContext.Provider
      value={{
        covers: sortedCovers,
        gameCategoriesList,
        pendingCovers,
        gameThemes: sortedGameThemes,
        pendingGameThemes: pendingGameThemesCMS,
        gameCategoriesMap,
        pendingGameCategoriesList:
          pendingGameCategoriesCMS || pendingCategories,
        depositCodes,
        pendingBonuses,
        pendingBonusGroups,
        bonuses,
        promocodes,
        pendingPromocodes,
        bonusGroups,
        jackpots,
        pendingThirdPartySummaries,
        thirdPartySummaries,
        customOffer,
        enableCurrencies,
        separatedCategoriesSectionData,
        separatedCategoriesSectionPending,
        depositBonuses,
        compointsRatesMoney: compointsRatesMoneyData,
        pendingCompointsRatesMoney,
        playerCompoints: playerCompointsS,
        fetchPlayerCompoints,
        pendingPlayerCompoints,
        isEmailConfirmationPopup,
        setEmailConfirmationPopupSeen,
        setEmailConfirmationPopupHeight,
        emailConfirmationPopupHeight,
        getFilteredByUserWalletsGames,
        autoRegistrationData,
        setAutoRegistrationData,
        cmsABTests: cmsABTestsData || [],
        pendingCmsAbTests,
        localizedTopCategoryId,
        localizedLuckyHitsCategoryId,
        cmsPaymentMethods,
        winningPrizeIndex,
        setWinningPrizeIndex,
        lootboxItem,
        setLootboxItem,
        wheelStatus,
        setWheelStatus,
        gameOpenBackUrl,
        setGameOpenBackUrl,
        crystalStatus,
        setCrystalStatus,
        isAuroraCrystalStarted,
        setIsAuroraCrystalStarted,
        handleChangeReceiveBonuses,
        pendingUpdateBonusSettings,
      }}
    >
      {children}
    </AddonsContext.Provider>
  );
};
